import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { MoreVertical } from 'lucide-react'
import type { RUserSession } from 'v2/types/api/auth'
import { IntegrationState } from '../utils/constants'
import type { ExternalAppRow } from './external-app-actions'

export type ToolMenuAction = 'enable' | 'disable' | 'delete'

interface ToolMenuEligibility {
  connections: ExternalAppRow[]
  currentUserId?: string
  canEdit: boolean
  canDelete: boolean
}

// Nothing unless every connection is yours: a partial act would leave the row showing a state
// that did not happen. Enable/Disable need every connection to agree.
export const getToolMenuActions = ({
  connections: rows,
  currentUserId,
  canEdit,
  canDelete,
}: ToolMenuEligibility): ToolMenuAction[] => {
  if (rows.length === 0) return []
  if (!currentUserId || rows.some((row) => row.integrated_by?.id !== currentUserId)) return []

  const actions: ToolMenuAction[] = []
  // `state` is a bare string off the wire; every comparison in this flow uppercases first.
  const states = rows.map((row) => row.state?.toUpperCase())
  if (canEdit) {
    if (states.every((state) => state === IntegrationState.DISABLED)) actions.push('enable')
    else if (states.every((state) => state === IntegrationState.ACTIVE)) actions.push('disable')
  }
  if (canDelete) actions.push('delete')
  return actions
}

const LABELS: Record<ToolMenuAction, string> = {
  enable: 'Enable',
  disable: 'Disable',
  delete: 'Delete',
}

interface ToolActionsMenuProps {
  row: ExternalAppRow
  targets: ExternalAppRow[]
  currentUser?: RUserSession
  canEdit: boolean
  canDelete: boolean
  onAction: (action: ToolMenuAction, targets: ExternalAppRow[]) => void
}

// The only row-level surface for these: the table's context menu returns nothing for group rows.
export const ToolActionsMenu = ({
  row,
  targets,
  currentUser,
  canEdit,
  canDelete,
  onAction,
}: ToolActionsMenuProps) => {
  const actions = getToolMenuActions({
    connections: targets,
    currentUserId: currentUser?.aom_uuid,
    canEdit,
    canDelete,
  })

  // A menu that opens onto nothing is worse than no menu.
  if (actions.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          // `data-[state=open]` keeps it visible once the pointer leaves the row for the menu.
          className="size-8 shrink-0 p-0 opacity-0 hover:bg-hv1 focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
          // The row's own click opens the detail sheet underneath the menu.
          onClick={(event) => event.stopPropagation()}
          data-testid="button-tool-actions"
          aria-label={`Actions for ${row.name}`}
        >
          <MoreVertical className="size-4 text-secondary" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom" onClick={(event) => event.stopPropagation()}>
        {actions.map((action) => (
          <DropdownMenuItem
            key={action}
            className={cn(
              'text-s-400',
              action === 'delete' && 'text-destructive focus:text-destructive',
            )}
            onSelect={() => onAction(action, targets)}
            data-testid={`menu-item-${action}-tool`}
          >
            {LABELS[action]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
